import React, { useState } from 'react';
import {
  BookOpen, Search, Plus, Edit, Trash2, Eye, Upload,
  FileText, Video, Image, Link2, Folder, Tag, Clock,
  CheckCircle, AlertTriangle, Globe, Building2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const categories = [
  { name: 'Infection Prevention', count: 45, icon: '🔬' },
  { name: 'Patient Safety', count: 38, icon: '🛡️' },
  { name: 'Quality Compliance', count: 32, icon: '✅' },
  { name: 'Clinical Operations', count: 28, icon: '📋' },
  { name: 'Training Materials', count: 24, icon: '📚' },
  { name: 'Regulatory Updates', count: 18, icon: '⚖️' },
];

const contentItems = [
  {
    id: 1,
    title: 'CLABSI Prevention Bundle - Complete Guide',
    type: 'document',
    category: 'Infection Prevention',
    status: 'published',
    lastUpdated: 'Aug 5, 2025',
    views: 12450,
    source: 'CDC Guidelines'
  },
  {
    id: 2,
    title: '5 Moments of Hand Hygiene Protocol',
    type: 'document',
    category: 'Infection Prevention',
    status: 'published',
    lastUpdated: 'Jul 28, 2025',
    views: 18920,
    source: 'WHO'
  },
  {
    id: 3,
    title: 'Fall Prevention - Post-Fall Assessment Guide',
    type: 'document',
    category: 'Patient Safety',
    status: 'published',
    lastUpdated: 'Aug 1, 2025',
    views: 8450,
    source: 'AHRQ'
  },
  {
    id: 4,
    title: 'Joint Commission Survey Prep Checklist',
    type: 'checklist',
    category: 'Quality Compliance',
    status: 'published',
    lastUpdated: 'Jul 20, 2025',
    views: 15670,
    source: 'Joint Commission'
  },
  {
    id: 5,
    title: 'Medication Reconciliation Best Practices',
    type: 'document',
    category: 'Patient Safety',
    status: 'draft',
    lastUpdated: 'Aug 8, 2025',
    views: 0,
    source: 'ISMP'
  },
  {
    id: 6,
    title: 'C. diff Isolation Precautions',
    type: 'document',
    category: 'Infection Prevention',
    status: 'published',
    lastUpdated: 'Aug 3, 2025',
    views: 9870,
    source: 'CDC/SHEA'
  },
];

const ContentPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Content & Knowledge Base</h1>
          <p className="text-gray-500 mt-1">Manage Ask Niko's knowledge base and clinical content</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Upload className="w-4 h-4" />
            Import
          </Button>
          <Button className="btn-primary gap-2">
            <Plus className="w-4 h-4" />
            Add Content
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <FileText className="w-4 h-4" />
            <span className="text-sm">Total Documents</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">185</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Published</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">172</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-yellow-600 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Drafts</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">13</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-[var(--niko-teal)] mb-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Total Views</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">892K</p>
        </div>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {categories.map((cat) => (
          <button
            key={cat.name}
            onClick={() => setSelectedCategory(cat.name)}
            className={`p-4 rounded-xl border text-left transition-all ${
              selectedCategory === cat.name
                ? 'bg-[var(--niko-teal)]/10 border-[var(--niko-teal)]'
                : 'bg-white border-gray-100 hover:border-gray-200'
            }`}
          >
            <span className="text-2xl mb-2 block">{cat.icon}</span>
            <p className="font-medium text-gray-900 text-sm">{cat.name}</p>
            <p className="text-xs text-gray-500">{cat.count} items</p>
          </button>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Content</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Category</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Source</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Views</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[var(--niko-teal)]/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[var(--niko-teal)]" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500">Updated {item.lastUpdated}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{item.source}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      item.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {item.status === 'published' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{item.views.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
